import { Labels } from "../../util/Translations"

const convertTagToSearchPart = (
    lut: Search.NormalizedNameLUT,
    searchTag: Search.Tag,
    userFullName: string,
    location: string | undefined,
    searchKeywords: Search.Keywords,
): SearchProtocol.SearchPart => {
    const result: SearchProtocol.SearchPart = {}

    let fieldType: Search.fieldType | undefined = undefined

    if (!!searchTag.exclude) {
        result.exclude = true
    }

    if (!!searchTag.include) {
        result.include = true
    }

    const { field, value } = searchTag
    // A fieldname specified
    if (!!field) {
        const searchText = value.trim()
        const searchField = field
        result.fieldLabel = searchField

        // swap out field name with ES name
        // TODO deal with missing ES names
        // TODO deal with name clashes
        // TODO deal with special search terms
        const normalizedName = normalizeName(searchField)
        const fieldInfo = lut[normalizedName]
        if (__DEBUG__) {
            console.info({
                ESNames: !!fieldInfo && fieldInfo.esFields,
                normalizedName: normalizedName,
                writtenName: searchField,
            })
        }
        if (!!fieldInfo) {
            result.fields = !!fieldInfo.esFields
                ? fieldInfo.esFields
                : [searchField]
            fieldType = fieldInfo.fieldType
        } else {
            throw Error(`Field with label "${searchField}" not found`)
        }

        // Star: Non-empty field.
        if (searchText === "*") {
            result.notEmpty = true
            result.queryType = "notEmpty" // TODO does this work?
            // No further parsing needed.
            return result
            // A search text specified.
        } else {
            result.text = searchText
        }

        // No fieldname specified.
    } else {
        result.text = value
    }

    // bool true
    if (fieldType === "booltype" && !!result.text) {
        if (hasKeyword(result.text, searchKeywords.search_bool_true)) {
            result.queryType = "bool"
            // ES only accepts lowercase true|false
            result.text = "true"
            return result
        }
        // bool false
        if (hasKeyword(result.text, searchKeywords.search_bool_false)) {
            result.queryType = "bool"
            // ES only accepts lowercase true|false
            result.text = "false"
            return result
        }
    }

    // time
    if (fieldType === "tftimetype" && !!result.text) {
        const parsedTime = parseTime(result.text.toLowerCase())
        if (parsedTime !== null) {
            result.queryType = "date"
            result.text = parsedTime
            return result
        }
        if (hasKeyword(result.text, ["am"])) {
            result.queryType = "date"
            result.text = undefined
            result.rangeFromEq = "00:00:00"
            result.rangeToEq = "11:59:59"
            return result
        }
        if (hasKeyword(result.text, ["pm"])) {
            result.queryType = "date"
            result.text = undefined
            result.rangeFromEq = "12:00:00"
            result.rangeToEq = "23:59:59"
            return result
        }
    }

    // date
    if (fieldType === "tfdatetype" && !!result.text) {
        const parsedDate = parseDate(result.text.toLowerCase(), searchKeywords)
        if (parsedDate !== null) {
            result.queryType = "date"
            result.text = parsedDate
            return result
        }
    }

    // number
    if (!!result.text && (!!!fieldType || fieldType === "numbertype")) {
        const parsedNumber = parseNumber(result.text)
        if (parsedNumber !== null) {
            result.queryType = "number"
            result.text = parsedNumber
            return result
        }
    }

    // range
    if (
        !!result.fields &&
        !!result.text &&
        !!fieldType &&
        ["numbertype", "tfdatetype", "tftimetype", "geotype"].indexOf(
            fieldType,
        ) >= 0
    ) {
        const parsedRange = parseRange(
            result.text.toLowerCase(),
            fieldType,
            location,
            searchKeywords,
        )
        if (parsedRange !== null) {
            result.queryType = parsedRange.queryType
            // don't need the text
            result.text = undefined
            result.rangeFrom = parsedRange.from
            result.rangeTo = parsedRange.to
            result.rangeFromEq = parsedRange.fromEq
            result.rangeToEq = parsedRange.toEq
            return result
        }
    }

    // default : text
    result.queryType = "text"

    if (
        fieldType === "keywordtype" &&
        hasKeyword(result.text, searchKeywords.search_userFullName)
    ) {
        if (__DEBUG__) {
            console.log(result.text + " = " + userFullName)
        }
        result.text = userFullName.toLowerCase()
        result.exact = true
    }

    // Single terms with numbers are exact.
    const textIsTermWithNumber =
        /\d/.test(result.text) && !/ /.test(result.text)
    if (!!textIsTermWithNumber) {
        result.exact = true
    }

    // Terms in quotes are exact.
    const quotesMatch = /^"(.*)"$/.exec(result.text)
    if (!!quotesMatch) {
        result.exact = true
        // don't include quotes in search
        result.text = quotesMatch[1].trim()
    }

    return result
}

const parseNumber = (text: string): string | null => {
    // int
    if (/^(-?)\d+$/.exec(text)) {
        return text
    }
    // float DOT
    if (/^(-?)\d+[.]\d+$/.exec(text)) {
        return text
    }
    // float COMMA
    if (/^(-?)\d+[,]\d+$/.exec(text)) {
        text = text.replace(",", ".")
        return text
    }
    // No parse
    return null
}

const parseTime = (text: string): string | null => {
    // (h)h(:mm)
    if (!!/^\d{1,2}(:\d{2})?$/.exec(text)) {
        return text
    }
    return null
}

const parseDate = (
    text: string,
    searchKeywords: Search.Keywords,
): string | null => {
    // dates
    const ddmmyyyyMatch = /^\d{1,2}\/\d{1,2}\/\d{4}$/.exec(text)
    if (!!ddmmyyyyMatch) {
        return text + "||/d"
    }
    const mmyyyyMatch = /^\d{1,2}\/\d{4}$/.exec(text)
    if (!!mmyyyyMatch) {
        return text + "||/M"
    }
    const yyyyMatch = /^\d{4}$/.exec(text)
    if (!!yyyyMatch) {
        return text + "||/y"
    }
    // keywords

    const todayMatch = hasKeyword(text, searchKeywords.search_date_now)
    if (!!todayMatch) {
        return "now/d"
    }
    const yesterdayMatch = hasKeyword(
        text,
        searchKeywords.search_date_yesterday,
    )
    if (!!yesterdayMatch) {
        return "now-1d/d"
    }
    const tomorrowMatch = hasKeyword(text, searchKeywords.search_date_tomorrow)
    if (!!tomorrowMatch) {
        return "now+1d/d"
    }

    // no match
    return null
}

const parseRange = (
    text: string,
    fieldType: Search.fieldType,
    location: string | undefined,
    searchKeywords: Search.Keywords,
): {
    from?: string
    to?: string
    fromEq?: string
    toEq?: string
    queryType: SearchProtocol.queryType
} | null => {
    const lessEqIndex = text.indexOf("<=")
    const lessEqMatch = lessEqIndex >= 0

    const greaterEqIndex = text.indexOf(">=")
    const greaterEqMatch = greaterEqIndex >= 0

    const lessIndex = text.indexOf("<")
    const lessMatch = lessIndex >= 0

    const greaterIndex = text.indexOf(">")
    const greaterMatch = greaterIndex >= 0

    // Don't match the dash in negative numbers like "-1" and negative coords like "-50.87,-4.71-50.88,4.69"
    const betweenIndex = text.match(/^(.*?[^,])-.+$/)?.[1]?.length || -1
    const betweenMatch = betweenIndex >= 0

    let toEq: string | undefined | null = undefined
    let fromEq: string | undefined | null = undefined

    let to: string | undefined | null = undefined
    let from: string | undefined | null = undefined

    let queryType: SearchProtocol.queryType | undefined = undefined

    if (lessEqMatch) {
        // remove <=
        toEq = text.slice(2).trim()
    } else if (greaterEqMatch) {
        // remove >=
        fromEq = text.slice(2).trim()
    } else if (lessMatch) {
        // remove <
        to = text.slice(1).trim()
    } else if (greaterMatch) {
        // remove >
        from = text.slice(1).trim()
    } else if (betweenMatch) {
        // remove - and split
        fromEq = text.slice(0, betweenIndex).trim()
        toEq = text.slice(betweenIndex + 1).trim()
    }

    switch (fieldType) {
        case "tftimetype":
            from = from && parseTime(from)
            to = to && parseTime(to)
            fromEq = fromEq && parseTime(fromEq)
            toEq = toEq && parseTime(toEq)
            queryType = "date"
            break
        case "tfdatetype":
            from = from && parseDate(from, searchKeywords)
            to = to && parseDate(to, searchKeywords)
            fromEq = fromEq && parseDate(fromEq, searchKeywords)
            toEq = toEq && parseDate(toEq, searchKeywords)
            queryType = "date"
            break
        case "numbertype":
            from = from && parseNumber(from)
            to = to && parseNumber(to)
            fromEq = fromEq && parseNumber(fromEq)
            toEq = toEq && parseNumber(toEq)
            queryType = "number"
            break
        case "geotype":
            queryType = "geo"
            if (!!fromEq && !!toEq && !from && !to) {
                break
            } else if (!!to && !fromEq && !toEq && !from) {
                fromEq = location
                toEq = to
                to = null
                break
            } else {
                console.error(`Error parsing geo range ${text}.`)
                from = to = fromEq = toEq = null
                break
            }
        default:
            throw new Error("Unknown fieldType")
    }

    return {
        from: from || undefined,
        to: to || undefined,
        fromEq: fromEq || undefined,
        toEq: toEq || undefined,
        queryType: queryType,
    }
}

export const getLocation = async (): Promise<string> => {
    const { LocationServicesNotAvailable, LocationPermissionsRequired } = Labels
    if ("geolocation" in navigator) {
        // Dummy request hack, else the actual one hangs if a Google Maps view is present on the page on Chrome.
        navigator.geolocation.getCurrentPosition(
            () => 0,
            () => 0,
            {},
        )
        return new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
                (success) => resolve(success),
                (error) => reject(error),
                {
                    // Else it is slower if a Google Maps view is present on the page on Chrome.
                    enableHighAccuracy: true,
                },
            )
        })
            .then(
                (value: GeolocationPosition) =>
                    `${value.coords.latitude},${value.coords.longitude}`,
            )
            .catch((error: GeolocationPositionError) => {
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        alert(LocationPermissionsRequired)
                        break
                    default:
                        alert(LocationServicesNotAvailable)
                        break
                }
                throw new Error(error.message)
            })
    } else {
        alert(LocationServicesNotAvailable)
        throw new Error("Browser geolocation is not available.")
    }
}

export const convertSearchToProtocol = (
    lut: Search.NormalizedNameLUT,
    searchTags: Search.Tag[],
    userFullName: string,
    location: string | undefined,
    searchKeywords: Search.Keywords,
): SearchProtocol.SearchQuery => {
    const result: SearchProtocol.SearchQuery = {
        input: searchTags.map((x) => x.input).join(";"),
    }

    const searchParts: SearchProtocol.SearchPart[] = searchTags.map((x) =>
        convertTagToSearchPart(lut, x, userFullName, location, searchKeywords),
    )

    // resolve search context
    const indexOfIn = searchParts.findIndex(
        (x) => !!x.fields && x.fields.toString() === ["in"].toString(),
    )
    if (indexOfIn >= 0) {
        // an "in:" context is defined
        // remove first "in:" from searchParts and set context
        result.searchContext = searchParts.splice(indexOfIn, 1)[0].text?.trim()
    } else {
        // result.searchContext = searchContextDefault == undefined
    }

    result.search = searchParts

    return result
}

export const buildNormalizedNameLUT = (
    translatedFieldScreenLabels: Search.FieldLabel[],
): Search.NormalizedNameLUT => {
    const lut: Search.NormalizedNameLUT = {}

    // Add field for "in:workspace"
    lut["in"] = {
        original: "in",
        esFields: undefined,
        fieldType: "keywordtype",
    }

    for (const {
        Code,
        Translation,
        EsFieldType,
    } of translatedFieldScreenLabels) {
        // Only consider fields with a display label
        if (Translation) {
            const normalizedName = normalizeName(Translation)
            const existingEntry: Search.NormalizedNameLUTEntry =
                lut[normalizedName]
            if (existingEntry == undefined) {
                // Make a new entry if the label isn't known yet
                lut[normalizedName] = {
                    esFields: [Code],
                    original: Translation,
                    fieldType: EsFieldType,
                }
            } else {
                // Else add the es field and resolve type conflicts
                mergeLUTEntries(existingEntry, Code, EsFieldType)
            }
        }
    }
    return lut
}

const mergeLUTEntries = (
    existingEntry: Search.NormalizedNameLUTEntry,
    Code: string,
    EsFieldType: Search.fieldType,
): void => {
    if (existingEntry.fieldType === EsFieldType) {
        existingEntry.esFields?.push(Code)
        return
    }
    // Resolve type conflicts
    if (__DEBUG__) {
        console.log("Type conflict", existingEntry, Code, EsFieldType)
    }
    const textLikeTypes: Search.fieldType[] = ["texttype", "keywordtype"]
    // Can downgrade either to texttype
    if (
        textLikeTypes.includes(existingEntry.fieldType) &&
        textLikeTypes.includes(EsFieldType)
    ) {
        existingEntry.esFields?.push(Code)
        existingEntry.fieldType = "texttype"
        return
    }
    // Can keep a text-like field, drop the other
    if (textLikeTypes.includes(existingEntry.fieldType)) {
        return
    } else if (textLikeTypes.includes(EsFieldType)) {
        existingEntry.fieldType = EsFieldType
        existingEntry.esFields = [Code]
        return
    }
    // No resolution possible, drop the new one.
    return
}

export const normalizeName = (name: string): string => {
    return name
        .toLowerCase()
        .normalize("NFD")
        .replace(/\s|\p{Diacritic}/gu, "")
}

const hasKeyword = (text: string, keywords: string[]): boolean => {
    return keywords.indexOf(text.toLowerCase()) >= 0
}
